import { Settings as SettingsIcon, Lock, Wind, Droplets } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMobileLayout } from '@/utils/useMobileLayout';
import { useAuth } from '@/contexts/AuthContext';
import type { MapSettings as MapSettingsState, TileLayerKey } from '@/hooks/useMapSettings';
import type { BoundaryRecord } from '@/components/maps/BoundariesOverlay';
import { cn } from '@/lib/utils';

/**
 * Controls how much of the settings UI is available on a given map.
 *
 * - `'full'` (default): every row — base layer, pins/polygons, parcels +
 *   parcel-No, boundaries (with optional per-boundary picker), optional wind.
 *   Public users still see the parcels/boundaries rows disabled with a
 *   "sign in as staff" tooltip — the legacy behaviour.
 * - `'basic'`: only base-layer + pins + polygons (+ wind if `showWindToggle`).
 *   No parcels or boundaries rows at all. Use on public/anonymous maps so
 *   we never fire the staff-only fetches behind the scenes.
 * - `'none'`: render nothing. The caller skips MapSettings entirely; useful
 *   for embedded GeoJSON renders (FPAMemberDetail, ParcelPicker) where the
 *   settings UI would be noise.
 */
export type MapSettingsGearMode = 'full' | 'basic' | 'none';

interface MapSettingsProps {
  settings: MapSettingsState;
  onChange: (patch: Partial<MapSettingsState>) => void;
  /** Position the gear inside a Leaflet map container. */
  className?: string;
  /** Which sections of the panel to expose. Defaults to `'full'`. */
  gearMode?: MapSettingsGearMode;
  /** Disable the parcels toggle (e.g. no tenant selected). */
  parcelsDisabledReason?: string;
  /**
   * When provided, the boundaries section renders a per-boundary checkbox
   * list. Pass the boundaries we already fetched (e.g. from BoundariesOverlay's
   * onBoundariesLoaded callback). Empty array → only the master toggle shows.
   */
  boundaries?: BoundaryRecord[];
  /** Enabled boundary IDs (null = all). */
  enabledBoundaryIds?: Set<number> | null;
  /** Called when a per-boundary checkbox toggles. */
  onToggleBoundary?: (boundaryId: number) => void;
  /** Disable the boundaries toggle (e.g. no tenant selected). */
  boundariesDisabledReason?: string;
  /** Show the wind-overlay toggle (forecast map only). */
  showWindToggle?: boolean;
}

export function MapSettings({
  settings,
  onChange,
  className,
  gearMode = 'full',
  parcelsDisabledReason,
  boundaries,
  enabledBoundaryIds,
  onToggleBoundary,
  boundariesDisabledReason,
  showWindToggle,
}: MapSettingsProps) {
  const { isMobile } = useMobileLayout();
  const { isAuthenticated, isStaff, can } = useAuth();
  const staffOk = isAuthenticated && isStaff();
  // Parcels key off the capability, not the staff role: a view-only role
  // (FPA_USER) holds parcel_list and is meant to see which parcels are member
  // parcels, but isStaff() excludes it — so the role check locked it out of the
  // one thing it's entitled to. The overlay only ever renders `linked_refs`,
  // which names no member.
  const parcelsOk = isAuthenticated && (isStaff() || can('parcel', 'list'));
  // Same for water sources: FPA_USER holds water_source_list and needs to see
  // hydrants and reservoirs on the map to fight a fire. /water-sources/map
  // returns WaterSourceMapItem, which carries no member fields at all.
  const waterSourcesOk = isAuthenticated && (isStaff() || can('water_source', 'list'));
  // In 'basic' mode we never offer parcels/boundaries — they're staff-only
  // overlays that would silently 401 against an anonymous session. Treat
  // gearMode as the gate; the staff check still backstops the 'full' path.
  const includeTenantOverlays = gearMode === 'full';
  const parcelsDisabled = !parcelsOk || Boolean(parcelsDisabledReason);
  const parcelsDisabledMessage = parcelsOk
    ? parcelsDisabledReason
    : 'Sign in as staff to view parcels.';
  const boundariesDisabled = !staffOk || Boolean(boundariesDisabledReason);
  const boundariesDisabledMessage = staffOk
    ? boundariesDisabledReason
    : 'Sign in as staff to view boundaries.';

  // 'none' → no gear at all. The caller skips MapSettings entirely.
  if (gearMode === 'none') return null;

  const body = (
    <div className="w-72 space-y-4">
      <div className="space-y-2">
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Base map
        </Label>
        <RadioGroup
          value={settings.tileLayer}
          onValueChange={(v) => onChange({ tileLayer: v as TileLayerKey })}
          className="grid grid-cols-2 gap-2"
        >
          <Label
            htmlFor="ms-street"
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted"
          >
            <RadioGroupItem value="street" id="ms-street" />
            Street
          </Label>
          <Label
            htmlFor="ms-satellite"
            className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted"
          >
            <RadioGroupItem value="satellite" id="ms-satellite" />
            Satellite
          </Label>
        </RadioGroup>
      </div>

      <div className="space-y-3">
        <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Overlays
        </Label>
        <ToggleRow
          id="ms-pins"
          label="Show pins"
          checked={settings.showPins}
          onChange={(v) => onChange({ showPins: v })}
        />
        <ToggleRow
          id="ms-polygons"
          label="Show burn areas"
          checked={settings.showPolygons}
          onChange={(v) => onChange({ showPolygons: v })}
        />
        {includeTenantOverlays && (
          <>
            <ToggleRow
              id="ms-parcels"
              label="Show parcels"
              checked={settings.showParcels}
              onChange={(v) => onChange({ showParcels: v })}
              disabled={parcelsDisabled}
              disabledReason={parcelsDisabledMessage}
            />
            <ToggleRow
              id="ms-parcel-no"
              label="Show parcel No"
              checked={settings.showParcelNo}
              onChange={(v) => onChange({ showParcelNo: v })}
              disabled={parcelsDisabled || !settings.showParcels}
              disabledReason={
                parcelsDisabled ? parcelsDisabledMessage : 'Enable Show parcels first.'
              }
            />
            <ToggleRow
              id="ms-boundaries"
              label="Show boundaries"
              checked={settings.showBoundaries}
              onChange={(v) => onChange({ showBoundaries: v })}
              disabled={boundariesDisabled}
              disabledReason={boundariesDisabledMessage}
            />
          </>
        )}
        {showWindToggle && (
          <ToggleRow
            id="ms-wind"
            label={
              <span className="flex items-center gap-1.5">
                <Wind className="h-3.5 w-3.5" />
                Wind direction overlay
              </span>
            }
            checked={settings.showWind}
            onChange={(v) => onChange({ showWind: v })}
          />
        )}
        {includeTenantOverlays && (
          <ToggleRow
            id="ms-water-sources"
            label={
              <span className="flex items-center gap-1.5">
                <Droplets className="h-3.5 w-3.5" />
                Water sources
              </span>
            }
            checked={settings.showWaterSources}
            onChange={(v) => onChange({ showWaterSources: v })}
            disabled={!waterSourcesOk}
            disabledReason={waterSourcesOk ? undefined : 'Sign in as staff to view water sources.'}
          />
        )}
      </div>

      {/* Per-boundary picker (only when staff + tenant + at least one boundary) */}
      {includeTenantOverlays &&
        staffOk &&
        settings.showBoundaries &&
        boundaries &&
        boundaries.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Boundaries ({boundaries.length})
            </Label>
            <div className="max-h-48 space-y-1.5 overflow-y-auto rounded-md border border-border p-2">
              {boundaries.map((b) => {
                const checked =
                  enabledBoundaryIds == null
                    ? true
                    : enabledBoundaryIds.has(b.id);
                return (
                  <div key={b.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`ms-boundary-${b.id}`}
                      checked={checked}
                      onCheckedChange={() => onToggleBoundary?.(b.id)}
                    />
                    <Label
                      htmlFor={`ms-boundary-${b.id}`}
                      className="flex-1 cursor-pointer truncate text-sm font-normal"
                      title={b.name}
                    >
                      {b.name}
                      {b.is_primary && (
                        <span className="ml-1 text-[10px] uppercase tracking-wide text-primary">
                          · primary
                        </span>
                      )}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </div>
  );

  const trigger = (
    <Button
      type="button"
      variant="secondary"
      size="icon"
      className={cn('shadow-md', className)}
      aria-label="Map settings"
    >
      <SettingsIcon className="h-4 w-4" />
    </Button>
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Map settings</SheetTitle>
          </SheetHeader>
          <div className="mt-4">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        {body}
      </PopoverContent>
    </Popover>
  );
}

interface ToggleRowProps {
  id: string;
  label: React.ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  disabledReason?: string;
}

function ToggleRow({ id, label, checked, onChange, disabled, disabledReason }: ToggleRowProps) {
  if (disabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-between gap-3 opacity-60">
              <Label htmlFor={id} className="flex items-center gap-1.5 text-sm">
                <Lock className="h-3 w-3" />
                {label}
              </Label>
              <Switch id={id} checked={false} disabled />
            </div>
          </TooltipTrigger>
          {disabledReason && <TooltipContent>{disabledReason}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    );
  }
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={id} className="text-sm">
        {label}
      </Label>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export default MapSettings;
